import { useCallback, useEffect, useRef, useState } from 'react';
import type { TiltReading } from '../utils/tilt';

/**
 * Inclinometro.
 *
 * Si usa l'ACCELEROMETRO, non il giroscopio: il telefono sta fermo appoggiato a
 * un piano, quindi l'unica accelerazione che sente è la gravità, e il vettore
 * gravità dà l'inclinazione assoluta senza deriva. Un giroscopio integrerebbe
 * l'errore e dopo un minuto direbbe il falso.
 *
 * Angoli nel riferimento del TELEFONO:
 *   roll  = atan2(x, z)   positivo quando il bordo destro scende
 *   pitch = atan2(y, z)   positivo quando il bordo alto scende
 * La conversione al riferimento del camper è in toCamper(): tenerla separata
 * evita di dover ricalibrare se un giorno il telefono viene appoggiato al contrario.
 */

const RAD = 180 / Math.PI;

/** Filtro passa-basso. Con eventi a 30-60 Hz questo alpha dà una costante di tempo
 *  di circa due decimi di secondo: abbastanza per togliere il tremolio, abbastanza
 *  poco da non far sembrare l'app in ritardo mentre si muove il telefono. */
const ALPHA = 0.09;

/** Finestra su cui si giudica la stabilità. */
const WINDOW_MS = 1200;
const STEADY_DEG = 0.06;

/** Il filtro gira a ogni evento, ma React viene svegliato al massimo a 20 Hz:
 *  la bolla si muove comunque fluida e il telefono non si scalda in mano. */
const PUBLISH_MS = 50;

export type TiltStatus =
  | 'idle'
  | 'running'
  /** la pagina non è in secure context: senza HTTPS il browser non dà i sensori */
  | 'insecure'
  | 'unsupported'
  | 'denied';

export type UseTilt = {
  reading: TiltReading | null;
  status: TiltStatus;
  /** Va chiamata dentro un gesto dell'utente: iOS 13+ pretende il permesso lì. */
  start: () => Promise<void>;
  stop: () => void;
};

type IosDeviceMotionEvent = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
};

export function useTilt(): UseTilt {
  const [reading, setReading] = useState<TiltReading | null>(null);
  const [status, setStatus] = useState<TiltStatus>('idle');

  const smooth = useRef<{ x: number; y: number; z: number } | null>(null);
  const history = useRef<{ t: number; roll: number; pitch: number }[]>([]);
  const lastPublish = useRef(0);
  const listening = useRef(false);
  // "L'utente vuole i sensori accesi": distinto da "sono accesi adesso", perché
  // in secondo piano si spengono per non consumare batteria.
  const wanted = useRef(false);

  const isSteady = useCallback(() => {
    const cut = Date.now() - WINDOW_MS;
    const recent = history.current.filter((h) => h.t >= cut);
    if (recent.length < 8) return false;

    for (const key of ['roll', 'pitch'] as const) {
      const values = recent.map((h) => h[key]);
      if (Math.max(...values) - Math.min(...values) > STEADY_DEG) return false;
    }
    return true;
  }, []);

  const onMotion = useCallback(
    (event: DeviceMotionEvent) => {
      const g = event.accelerationIncludingGravity;
      if (!g || g.x == null || g.y == null || g.z == null) return;

      const prev = smooth.current;
      smooth.current = prev
        ? {
            x: prev.x + ALPHA * (g.x - prev.x),
            y: prev.y + ALPHA * (g.y - prev.y),
            z: prev.z + ALPHA * (g.z - prev.z),
          }
        : { x: g.x, y: g.y, z: g.z };

      const s = smooth.current;
      const roll = Math.atan2(s.x, s.z) * RAD;
      const pitch = Math.atan2(s.y, s.z) * RAD;

      const now = Date.now();
      history.current.push({ t: now, roll, pitch });
      if (history.current.length > 200) history.current.shift();

      if (now - lastPublish.current < PUBLISH_MS) return;
      lastPublish.current = now;
      setReading({ roll, pitch, steady: isSteady(), raw: { ...s } });
    },
    [isSteady],
  );

  const attach = useCallback(() => {
    if (listening.current) return;
    window.addEventListener('devicemotion', onMotion);
    listening.current = true;
  }, [onMotion]);

  const detach = useCallback(() => {
    if (!listening.current) return;
    window.removeEventListener('devicemotion', onMotion);
    listening.current = false;
  }, [onMotion]);

  const start = useCallback(async () => {
    if (!window.isSecureContext) {
      setStatus('insecure');
      return;
    }
    if (typeof window.DeviceMotionEvent === 'undefined') {
      setStatus('unsupported');
      return;
    }

    const ios = DeviceMotionEvent as IosDeviceMotionEvent;
    if (typeof ios.requestPermission === 'function') {
      let granted: string;
      try {
        granted = await ios.requestPermission();
      } catch {
        // Succede se la chiamata non parte da un gesto utente, oppure fuori da HTTPS.
        granted = 'denied';
      }
      if (granted !== 'granted') {
        setStatus('denied');
        return;
      }
    }

    smooth.current = null;
    history.current = [];
    wanted.current = true;
    attach();
    setStatus('running');
  }, [attach]);

  const stop = useCallback(() => {
    wanted.current = false;
    detach();
    setStatus('idle');
  }, [detach]);

  // I sensori consumano batteria: si spengono quando la pagina va in secondo piano.
  useEffect(() => {
    const onVisibility = () => {
      if (!wanted.current) return;
      if (document.hidden) {
        detach();
      } else {
        // Il filtro riparte da zero: al ritorno la prima lettura è quella buona,
        // non una media con quello che il telefono sentiva un minuto fa.
        smooth.current = null;
        history.current = [];
        attach();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      detach();
    };
  }, [attach, detach]);

  return { reading, status, start, stop };
}
